import { useState, useCallback, useMemo, useEffect } from 'react';
import { Sparkles, RefreshCw, Copy, Check, Tag, Mic, Layout, Mail, Share2, Target, ChevronDown, ChevronRight } from 'lucide-react';
import { AIProgressLoader } from '../common/AIProgressLoader';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useSimulatorStore } from '../../stores/useSimulatorStore';
import { buildSystemPrompt } from '../../data/aiContext';
import { apiFetch } from '../../lib/api';
import { formatPercent } from '../../lib/formatters';
import { getActiveClusters, getTopFeaturesInPackage } from '../../lib/featureClusters';
import type { PackageConfig, PnLResult } from '../../types';
import type { BuyerProxyResult } from '../../lib/buyerProxy';
import type { StrategicDirection } from '../../lib/strategicDirections';
import { ChannelStrategy } from './ChannelStrategy';
import { CompetitivePositioningMap } from './CompetitivePositioningMap';
import { MarketingPackageExport } from './MarketingPackageExport';
import { AdCreativePreview } from './AdCreativePreview';

interface Props {
 config: PackageConfig;
 pnl: PnLResult;
 buyerProxy: BuyerProxyResult;
 segmentDemand: { segmentName: string; segmentRate: number; weight: number }[];
 direction: StrategicDirection;
 savedContent?: string | null;
 onContentGenerated?: (content: string) => void;
 savedAds?: Record<string, { image: string; mimeType: string; format: string }>;
 onAdsChanged?: (ads: Record<string, { image: string; mimeType: string; format: string }>) => void;
}

function CopyBtn({ text, label }: { text: string; label?: string }) {
 const [copied, setCopied] = useState(false);
 return (
 <button onClick={() => { navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 2000); }}
 className="flex items-center gap-1 px-2 py-1 text-micro rounded border border-border text-muted hover:text-primary hover:bg-surface transition-colors shrink-0">
 {copied ? <Check size={10} className="text-green-500" /> : <Copy size={10} />}
 {copied ? 'Copied' : label || 'Copy'}
 </button>
 );
}

interface ParsedSection { title: string; content: string; }

function extractHeadline(sections: ParsedSection[]): string {
 const taglineSection = sections.find(s => s.title.toLowerCase().includes("tagline"));
 if (taglineSection) {
 const match = taglineSection.content.match(/"([^"]{3,60})"/);
 if (match) return match[1];
 }
 return "Your body, finally understood.";
}

function parseSections(md: string): ParsedSection[] {
 const sections: ParsedSection[] = [];
 const lines = md.split('\n');
 let title = '';
 let buf: string[] = [];
 for (const line of lines) {
 const m = line.match(/^## (.+)/);
 if (m) {
 if (title) sections.push({ title, content: buf.join('\n').trim() });
 title = m[1].trim();
 buf = [];
 } else {
 buf.push(line);
 }
 }
 if (title) sections.push({ title, content: buf.join('\n').trim() });
 return sections;
}

function sectionMeta(title: string): { icon: React.ReactNode; hex: string } {
 const t = title.toLowerCase();
 if (t.includes('tagline')) return { icon: <Tag size={15} />, hex: '#8B5CF6' };
 if (t.includes('pitch') || t.includes('elevator')) return { icon: <Mic size={15} />, hex: '#3B82F6' };
 if (t.includes('landing') || t.includes('headline') || t.includes('value prop')) return { icon: <Layout size={15} />, hex: '#14B8A6' };
 if (t.includes('email')) return { icon: <Mail size={15} />, hex: '#F59E0B' };
 if (t.includes('social')) return { icon: <Share2 size={15} />, hex: '#EC4899' };
 if (t.includes('campaign') || t.includes('brief')) return { icon: <Target size={15} />, hex: '#7C3AED' };
 return { icon: <Sparkles size={15} />, hex: '#14B8A6' };
}

function SectionCard({ section, index }: { section: ParsedSection; index: number }) {
 const [open, setOpen] = useState(true);
 const meta = sectionMeta(section.title);
 return (
 <div
  className="rounded-xl border border-border/60 overflow-hidden transition-shadow hover:shadow-sm"
  style={{ '--section-accent': meta.hex } as React.CSSProperties}
 >
  {/* Header */}
  <button
   onClick={() => setOpen(!open)}
   className="w-full flex items-center gap-3 px-5 py-4 text-left transition-colors"
   style={{ background: open ? `color-mix(in srgb, ${meta.hex} 4%, transparent)` : undefined }}
  >
   <div
    className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0 text-xs font-bold text-white shadow-sm"
    style={{ background: meta.hex }}
   >
    {index + 1}
   </div>
   <span style={{ color: meta.hex }} className="shrink-0">{meta.icon}</span>
   <h3 className="text-sm font-bold text-primary flex-1 truncate">{section.title}</h3>
   <CopyBtn text={section.content} label="Copy section" />
   {open ? <ChevronDown size={14} className="text-muted" /> : <ChevronRight size={14} className="text-muted" />}
  </button>

  {/* Content */}
  {open && (
   <div className="px-5 pb-5">
    <div
     className="rounded-lg p-5 border border-border/40"
     style={{ backgroundColor: 'var(--color-card)' }}
    >
     <div className="marketing-prose">
      <Markdown remarkPlugins={[remarkGfm]}>{section.content}</Markdown>
     </div>
    </div>
   </div>
  )}
 </div>
 );
}

function buildCopyPrompt(config: PackageConfig, pnl: PnLResult, proxy: BuyerProxyResult, direction: StrategicDirection): string {
 const d = proxy.demographics;
 const topFeatures = getTopFeaturesInPackage(config.enabledFeatures, 3);
 const clusters = getActiveClusters(config.enabledFeatures);
 const topGoals = d.topGoals.slice(0, 3).map(g => `${g.label} (${g.percentage}%)`).join(', ');
 const topWtp = d.topWtpFactors.slice(0, 3).map(f => `${f.label} (${f.percentage}%)`).join(', ');
 const goalQuotes = d.topGoals.flatMap(g => g.sampleQuotes).slice(0, 4).map(q => `"${q}"`).join('\n');
 const wtpQuotes = d.topWtpFactors.flatMap(f => f.sampleQuotes).slice(0, 4).map(q => `"${q}"`).join('\n');

 return `You are a senior copywriter generating consumer-facing marketing materials for AlterMe. Every piece of copy MUST align with the chosen strategic direction below. The tone, language, and angle must be consistent throughout.

STRATEGIC DIRECTION CHOSEN: "${direction.name}"
- Positioning: ${direction.positioningStatement}
- Tone: ${direction.tone}
- Lead pillar: ${direction.leadPillar}
- Emotional trigger: ${direction.emotionalTrigger}
- Risk level: ${direction.riskLevel}
- Target audience: ${direction.targetAudience || `${d.femalePercent}% female, median age ${d.medianAge}`}

PACKAGE: "${config.name}" at $${config.price} with ${config.enabledFeatures.length} features
- Features: ${config.enabledFeatures.join(', ')}
- Top demand drivers: ${topFeatures.map(f => `${f.name} (+${formatPercent(f.lift)} lift)`).join(', ')}
- Conjoint demand rate: ${formatPercent(pnl.adjustedChoiceRate)}

CONSUMER (${proxy.count} buyer-proxy respondents):
- ${d.femalePercent}% Female, median age ${d.medianAge}, age ${d.ageRange[0]}-${d.ageRange[1]}
- ${d.brandAwareness.neverHeardPercent}% never heard of AlterMe
- Top health goals: ${topGoals}
- Top WTP factors: ${topWtp}
- Goal quotes: ${goalQuotes}
- WTP quotes: ${wtpQuotes}

Generate ALL of the following in well-formatted markdown. Each major section MUST start with ## (H2). Use ### (H3) for subsections within. Use **bold** for emphasis and > for quotes.

## Taglines
Generate 5 tagline options (3-8 words each). For each, format as:
1. **"Tagline here"** — Tone: [tone]. Maps to: [data point].

## Elevator Pitch — 15 Seconds
One paragraph, under 30 words.

## Elevator Pitch — 30 Seconds
One paragraph, under 75 words. Include demand proof (${formatPercent(pnl.adjustedChoiceRate)} chose this).

## Elevator Pitch — 60 Seconds
Two paragraphs, under 150 words. Add awareness gap (${d.brandAwareness.neverHeardPercent}% unaware) and CTA.

## Landing Page Copy
### Headlines
3 headline options (numbered).
### Subheadlines
3 subheadline options (numbered).
### Value Propositions
${clusters.map(c => `**${c.name}**: ${c.features.join(', ')}`).join('\n')}
For each cluster: a headline, 2-3 sentences, and a proof point.

## Email Sequence
### Email 1: The Problem (Send Immediately)
3 subject line options, opening hook, 3 body bullets, CTA.
### Email 2: The Guide (Day 3)
Same format.
### Email 3: The Success (Day 7)
Same format.

## Social Media Posts
### Post 1: Transformation Story
Full post copy with hashtags.
### Post 2: Data-Driven Stat
Full post copy with feature lift data and hashtags.
### Post 3: Objection Buster
Open with a real WTP quote. Full post copy with hashtags.

## Campaign Briefs
### Brief A: Awareness Campaign
Objective, core insight, campaign idea, target, lead channel, message angle, duration.
### Brief B: Conversion Campaign
Same format.

CRITICAL: Every piece of copy must reflect the "${direction.name}" direction with "${direction.tone}" tone. Do NOT write generic marketing.`;
}

export function MarketingWorkspace({ config, pnl, buyerProxy, segmentDemand, direction, savedContent, onContentGenerated, savedAds, onAdsChanged }: Props) {
 const [content, setContent] = useState<string | null>(savedContent ?? null);
 const [loading, setLoading] = useState(false);
 const [error, setError] = useState<string | null>(null);

 // Load saved content when props change
 useEffect(() => {
 setContent(savedContent ?? null);
 }, [savedContent, direction]);

 const sections = useMemo(() => content ? parseSections(content) : [], [content]);

 const generate = useCallback(async () => {
 setLoading(true);
 setError(null);
 try {
 const state = {
 assumptions: useSimulatorStore.getState().assumptions,
 packages: useSimulatorStore.getState().packages,
 activeTab: 'marketing' as const,
 selectedPrice: useSimulatorStore.getState().selectedPrice,
 };
 const systemPrompt = buildSystemPrompt(state);
 const prompt = buildCopyPrompt(config, pnl, buyerProxy, direction);

 const res = await apiFetch('/api/insights', {
 method: 'POST',
 body: JSON.stringify({ prompt, system: systemPrompt, maxTokens: 8192 }),
 });
 if (!res.ok) throw new Error(`API error: ${res.status}`);
 const data = await res.json();
 setContent(data.content);
 onContentGenerated?.(data.content);
 } catch (e) {
 setError(e instanceof Error ? e.message : 'Failed to generate');
 } finally {
 setLoading(false);
 }
 }, [config, pnl, buyerProxy, direction, segmentDemand, onContentGenerated]);

 return (
 <div>
 <div className="flex items-center justify-between mb-3">
 <div className="text-xs font-semibold uppercase tracking-wider text-muted">Step 3: Marketing Materials</div>
 <div className="flex items-center gap-2">
 {content && <CopyBtn text={content} label="Copy all" />}
 {content && (
 <button onClick={generate} disabled={loading}
 className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-border text-secondary hover:text-primary hover:bg-surface transition-colors disabled:opacity-50">
 <RefreshCw size={12} className={loading ? 'animate-spin' : ''} />
 Regenerate
 </button>
 )}
 <MarketingPackageExport config={config} pnl={pnl} buyerProxy={buyerProxy} direction={direction} generatedContent={content} assumptions={useSimulatorStore.getState().assumptions} />
 </div>
 </div>

 {/* Active Direction Banner */}
 <div className="rounded-xl border border-chart-teal/30 bg-chart-teal/5 p-4 mb-5">
 <div className="flex items-start gap-3">
 <div className="w-8 h-8 rounded-lg bg-chart-teal/10 flex items-center justify-center shrink-0 mt-0.5">
 <Sparkles size={16} className="text-chart-teal" />
 </div>
 <div className="flex-1 min-w-0">
 <div className="text-xs font-semibold uppercase tracking-wider text-chart-teal mb-0.5">Active Direction</div>
 <h4 className="text-sm font-bold text-primary">{direction.name}</h4>
 <p className="text-sm text-secondary mt-1">{direction.positioningStatement}</p>
 <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2">
 <span className="text-xs text-muted"><span className="font-semibold">Tone:</span> {direction.tone}</span>
 <span className="text-xs text-muted"><span className="font-semibold">Lead:</span> {direction.leadPillar}</span>
 <span className="text-xs text-muted"><span className="font-semibold">Risk:</span> {direction.riskLevel}</span>
 </div>
 {direction.targetAudience && (
 <p className="text-xs text-muted mt-1.5 italic border-t border-chart-teal/10 pt-1.5">Target: {direction.targetAudience}</p>
 )}
 </div>
 </div>
 </div>

 {/* Generate Button */}
 {!content && !loading && !error && (
 <div className="rounded-xl border-2 border-dashed border-border text-center py-12">
 <Sparkles size={32} className="text-chart-teal mx-auto mb-3" />
 <h3 className="text-sm font-semibold text-primary mb-1">Generate Marketing Materials</h3>
 <p className="text-xs text-muted mb-5 max-w-md mx-auto">
 GHOST AI will create taglines, elevator pitches, landing page copy, an email sequence, social posts, and campaign briefs — all aligned to your "{direction.name}" direction.
 </p>
 <button onClick={generate} className="px-6 py-3 text-sm font-medium rounded-xl bg-chart-teal text-white hover:bg-teal-600 transition-colors inline-flex items-center gap-2 shadow-sm">
 <Sparkles size={14} />
 Generate Materials
 </button>
 </div>
 )}

 {loading && (
 <div className="rounded-xl border border-border py-14">
 <AIProgressLoader
 estimatedSeconds={45}
 messages={[
 'Studying your buyer\u2019s deepest desires\u2026',
 'Channeling Mad Men energy (with better data)\u2026',
 'Distilling 1,001 survey responses into one story\u2026',
 'Writing headlines your audience can\u2019t ignore\u2026',
 'Crafting taglines that stick like earworms\u2026',
 'A/B testing copy in an alternate universe\u2026',
 'Brewing the perfect elevator pitch\u2026',
 'Making your landing page impossible to scroll past\u2026',
 'Sprinkling social proof throughout\u2026',
 'Writing emails people actually want to open\u2026',
 'Finding the perfect subject line (attempt #47)\u2026',
 'Adding urgency without the sleaze\u2026',
 'Turning data points into desire\u2026',
 'Matching tone to your target audience\u2026',
 'Drafting campaign briefs a CMO would approve\u2026',
 'Creating social posts worth sharing\u2026',
 'Polishing every word to a shine\u2026',
 'Almost there \u2014 your marketing kit is coming together\u2026',
 ]}
 />
 </div>
 )}

 {error && (
 <div className="rounded-xl border border-metric-negative/20 bg-metric-negative/5 p-4 mb-4 text-metric-negative text-xs flex items-center gap-2">
 {error}
 <button onClick={generate} className="ml-auto underline">Retry</button>
 </div>
 )}

 {sections.length > 0 && !loading && (
 <div className="space-y-4 mb-6">
 {sections.map((section, i) => (
 <SectionCard key={i} section={section} index={i} />
 ))}
 <p className="text-micro text-muted italic text-center pt-2">
 Generated by GHOST AI · Direction: "{direction.name}" · Source: CBC Conjoint Study (n=1,001) · Review and customize before deployment.
 </p>
 </div>
 )}

 {content && sections.length === 0 && !loading && (
 <div className="card mb-6">
 <div className="marketing-prose">
 <Markdown remarkPlugins={[remarkGfm]}>{content}</Markdown>
 </div>
 </div>
 )}

 {/* Ad Creative Previews */}
 {content && sections.length > 0 && !loading && (
 <div className="mb-6">
 <AdCreativePreview
 headline={extractHeadline(sections)}
 subheadline={direction.positioningStatement}
 cta={`Start Your ${config.price >= 1499 ? "Premium" : config.price >= 999 ? "Core" : "Starter"} Journey`}
 price={`$${config.price.toLocaleString()}`}
 packageName={config.name}
 direction={direction}
 savedAds={savedAds}
 onAdsChanged={onAdsChanged}
 />
 </div>
 )}

 <ChannelStrategy config={config} buyerProxy={buyerProxy} />
 <CompetitivePositioningMap />
 </div>
 );
}
